import type { KataScoringDefinition, ScoringCriterion } from './types'

function c(
  id: string,
  text: string,
  suggested: ScoringCriterion['suggested'],
  hint?: string,
): ScoringCriterion {
  return hint ? { id, text, suggested, hint } : { id, text, suggested }
}

const generalCeremony: ScoringCriterion[] = [
  c('goshin-gen-weapons-place', 'Way of correctly placing and collecting the weapons', 'medium'),
  c('goshin-gen-weapon-use', 'Weapon use (each technique)', 'medium'),
  c('goshin-gen-direction', 'Direction of a technique', 'medium'),
  c('goshin-gen-armlock-look', 'While executing an arm lock Tori looks to his front', 'small'),
  c('goshin-gen-kiai', 'Kiai', 'small_medium', 'Small if unnecessary; Medium if absent'),
  c('goshin-gen-submit', 'Submitting tapping twice', 'small'),
  c('goshin-gen-weapons-mat', 'Correctly placing weapons on the mat at the beginning and collecting them at the end', 'small_medium'),
]

/** Practice judging: 22 scored elements (max 220); opening/closing bows are catalog-only. */
export const kodokanGoshinJutsuScoring: KataScoringDefinition = {
  kataId: 'kodokan-goshin-jutsu',
  maxPerJudge: 220,
  maxPanelOfThree: 660,
  elements: [
    {
      id: 'goshin-general',
      kind: 'ceremony',
      name: 'General elements',
      nameJa: '一般観察事項',
      criteria: generalCeremony,
    },
    {
      id: 'goshin-ryote-dori',
      kind: 'technique',
      name: 'Ryote-dori',
      nameJa: '両手取',
      techniqueId: 'goshin-ryote-dori',
      groupId: 'unarmed-holds',
      criteria: [
        c('goshin-ryote-dori-1', 'Uke steps forward with left foot, grabs Tori’s wrists, attacks Tori’s groin with his right knee (kiai).', 'small_medium'),
        c('goshin-ryote-dori-2', 'Tori steps to his left rear, frees the right wrist, Te-gatana to Uke’s temple (kiai), then Kote-hineri and standing arm control looking forward.', 'small_medium'),
        c('goshin-ryote-dori-3', 'Uke taps on his left leg twice to submit and should not bend too much.', 'small_medium'),
      ],
    },
    {
      id: 'goshin-hidari-eri-dori',
      kind: 'technique',
      name: 'Hidari-eri-dori',
      nameJa: '左襟取',
      techniqueId: 'goshin-hidari-eri-dori',
      groupId: 'unarmed-holds',
      criteria: [
        c('goshin-hidari-eri-dori-1', 'Uke grasps Tori’s left lapel and tries to push Tori down on the back.', 'small_medium'),
        c('goshin-hidari-eri-dori-2', 'Tori opens his lapel, Me-tsubushi, Kote-hineri, brings Uke down and applies Ude-hishigi-te-gatame.', 'small_medium'),
        c('goshin-hidari-eri-dori-3', 'Uke taps the mat twice while lying on his stomach.', 'small_medium'),
      ],
    },
    {
      id: 'goshin-migi-eri-dori',
      kind: 'technique',
      name: 'Migi-eri-dori',
      nameJa: '右襟取',
      techniqueId: 'goshin-migi-eri-dori',
      groupId: 'unarmed-holds',
      criteria: [
        c('goshin-migi-eri-dori-1', 'Uke grasps Tori’s right lapel and pulls strongly down stepping back.', 'small_medium'),
        c('goshin-migi-eri-dori-2', 'Tori steps forward and uppercuts Uke’s chin (kiai).', 'small_medium'),
        c('goshin-migi-eri-dori-3', 'Tori applies Kote-gaeshi and throws Uke forward.', 'small_medium'),
      ],
    },
    {
      id: 'goshin-kata-ude-dori',
      kind: 'technique',
      name: 'Kata-ude-dori',
      nameJa: '片腕取',
      techniqueId: 'goshin-kata-ude-dori',
      groupId: 'unarmed-holds',
      criteria: [
        c('goshin-kata-ude-dori-1', 'Starting position: Uke at Tori’s right back corner; applies arm lock to Tori’s right elbow from behind and advances.', 'small_medium'),
        c('goshin-kata-ude-dori-2', 'On the third step Tori half-turns and kicks the inside of Uke’s left knee (kiai).', 'small_medium'),
        c('goshin-kata-ude-dori-3', 'Tori applies Ude-hishigi-waki-gatame standing, looking forward; Uke taps twice.', 'small_medium'),
      ],
    },
    {
      id: 'goshin-ushiro-eri-dori',
      kind: 'technique',
      name: 'Ushiro-eri-dori',
      nameJa: '後襟取',
      techniqueId: 'goshin-ushiro-eri-dori',
      groupId: 'unarmed-holds',
      criteria: [
        c('goshin-ushiro-eri-dori-1', 'Uke grabs the back of Tori’s collar from behind and tries to pull him down.', 'small_medium'),
        c('goshin-ushiro-eri-dori-2', 'Tori turns, protects the face, punches solar plexus (kiai), then Ude-hishigi-ude-gatame.', 'small_medium'),
        c('goshin-ushiro-eri-dori-3', 'Uke taps on his left leg twice and should not bend too much.', 'small_medium'),
      ],
    },
    {
      id: 'goshin-ushiro-jime',
      kind: 'technique',
      name: 'Ushiro-jime',
      nameJa: '後絞',
      techniqueId: 'goshin-ushiro-jime',
      groupId: 'unarmed-holds',
      criteria: [
        c('goshin-ushiro-jime-1', 'Uke attempts standing Hadaka-jime from behind.', 'small_medium'),
        c('goshin-ushiro-jime-2', 'Tori defends, twists, pulls Uke down and holds with Ude-hishigi-te-gatame.', 'small_medium'),
        c('goshin-ushiro-jime-3', 'Uke taps twice on the mat while lying on his stomach.', 'small_medium'),
      ],
    },
    {
      id: 'goshin-kakae-dori',
      kind: 'technique',
      name: 'Kakae-dori',
      nameJa: '抱取',
      techniqueId: 'goshin-kakae-dori',
      groupId: 'unarmed-holds',
      criteria: [
        c('goshin-kakae-dori-1', 'Uke holds around Tori’s waist over both arms from behind.', 'small_medium'),
        c('goshin-kakae-dori-2', 'Tori stomps Uke’s instep (kiai), releases the hold, applies arm lock and throws Uke diagonally forward.', 'small_medium'),
        c('goshin-kakae-dori-3', 'Uke doesn’t stand up; Tori takes shizen-tai.', 'small_medium'),
      ],
    },
    {
      id: 'goshin-naname-uchi',
      kind: 'technique',
      name: 'Naname-uchi',
      nameJa: '斜打',
      techniqueId: 'goshin-naname-uchi',
      groupId: 'unarmed-distance',
      criteria: [
        c('goshin-naname-uchi-1', 'Uke strikes toward Tori’s left temple with right fist (kiai).', 'small_medium'),
        c('goshin-naname-uchi-2', 'Tori dodges/parries, uppercuts chin (kiai), then Osoto-otoshi with Yahazu to the throat.', 'small_medium'),
      ],
    },
    {
      id: 'goshin-ago-tsuki',
      kind: 'technique',
      name: 'Ago-tsuki',
      nameJa: '顎突',
      techniqueId: 'goshin-ago-tsuki',
      groupId: 'unarmed-distance',
      criteria: [
        c('goshin-ago-tsuki-1', 'Uke uppercuts toward Tori’s chin (kiai).', 'small_medium'),
        c('goshin-ago-tsuki-2', 'Tori deflects, locks the arm, and throws Uke diagonally forward; Uke doesn’t stand up.', 'small_medium'),
      ],
    },
    {
      id: 'goshin-ganmen-tsuki',
      kind: 'technique',
      name: 'Ganmen-tsuki',
      nameJa: '顔面突',
      techniqueId: 'goshin-ganmen-tsuki',
      groupId: 'unarmed-distance',
      criteria: [
        c('goshin-ganmen-tsuki-1', 'Uke left-stance punches toward Tori’s face (kiai).', 'small_medium'),
        c('goshin-ganmen-tsuki-2', 'Tori punches ribs (kiai), applies Hadaka-jime from behind; Uke taps twice with the foot.', 'small_medium'),
      ],
    },
    {
      id: 'goshin-mae-geri',
      kind: 'technique',
      name: 'Mae-geri',
      nameJa: '前蹴',
      techniqueId: 'goshin-mae-geri',
      groupId: 'unarmed-distance',
      criteria: [
        c('goshin-mae-geri-1', 'Uke Mae-geri to Tori’s groin with the sole of the right foot (kiai).', 'small_medium'),
        c('goshin-mae-geri-2', 'Tori avoids, controls the foot/ankle, and throws Uke onto the back.', 'small_medium'),
      ],
    },
    {
      id: 'goshin-yoko-geri',
      kind: 'technique',
      name: 'Yoko-geri',
      nameJa: '横蹴',
      techniqueId: 'goshin-yoko-geri',
      groupId: 'unarmed-distance',
      criteria: [
        c('goshin-yoko-geri-1', 'Uke attempts Yoko-geri to Tori’s side (kiai).', 'small_medium'),
        c('goshin-yoko-geri-2', 'Tori sweeps the kick away, steps behind, and pulls Uke down onto the back.', 'small_medium'),
      ],
    },
    {
      id: 'goshin-tsukkake',
      kind: 'technique',
      name: 'Tsukkake',
      nameJa: '突掛',
      techniqueId: 'goshin-tsukkake',
      groupId: 'dagger',
      criteria: [
        c('goshin-tsukkake-1', 'Uke draws the dagger (edge up) and prepares at half-step distance.', 'small_medium'),
        c('goshin-tsukkake-2', 'Tori moves to Uke’s left, Me-tsubushi (kiai), then Ude-hishigi-te-gatame; Uke taps twice holding the dagger.', 'small_medium'),
        c('goshin-tsukkake-3', 'Weapon return: dagger given back correctly when applicable.', 'small_medium'),
      ],
    },
    {
      id: 'goshin-choku-tsuki',
      kind: 'technique',
      name: 'Choku-zuki',
      nameJa: '直突',
      techniqueId: 'goshin-choku-tsuki',
      groupId: 'dagger',
      criteria: [
        c('goshin-choku-tsuki-1', 'Uke advances and attempts to stab Tori in the stomach (kiai).', 'small_medium'),
        c('goshin-choku-tsuki-2', 'Tori parries, uppercuts (kiai), applies Ude-hishigi-waki-gatame standing looking forward; Uke taps twice.', 'small_medium'),
      ],
    },
    {
      id: 'goshin-naname-tsuki',
      kind: 'technique',
      name: 'Naname-zuki',
      nameJa: '斜突',
      techniqueId: 'goshin-naname-tsuki',
      groupId: 'dagger',
      criteria: [
        c('goshin-naname-tsuki-1', 'Uke reverse-grip dagger strike to left side of the neck (kiai).', 'small_medium'),
        c('goshin-naname-tsuki-2', 'Tori dodges, throws with Kote-gaeshi, te-gatame control, takes dagger without gripping the blade edge; Uke taps twice.', 'small_medium'),
      ],
    },
    {
      id: 'goshin-furi-age',
      kind: 'technique',
      name: 'Furi-age',
      nameJa: '振上',
      techniqueId: 'goshin-furi-age',
      groupId: 'staff',
      criteria: [
        c('goshin-furi-age-1', 'Uke raises the staff above the head to attack.', 'small_medium'),
        c('goshin-furi-age-2', 'Tori blocks, pushes chin (kiai), throws with O-soto-gari holding collar to the ground.', 'small_medium'),
      ],
    },
    {
      id: 'goshin-furi-oroshi',
      kind: 'technique',
      name: 'Furi-oroshi',
      nameJa: '振下',
      techniqueId: 'goshin-furi-oroshi',
      groupId: 'staff',
      criteria: [
        c('goshin-furi-oroshi-1', 'Uke diagonal staff attack to Tori’s left yokomen (kiai).', 'small_medium'),
        c('goshin-furi-oroshi-2', 'Tori avoids, atemi (kiai), takes staff and pushes Uke down backwards.', 'small_medium'),
      ],
    },
    {
      id: 'goshin-morote-tsuki',
      kind: 'technique',
      name: 'Morote-tsuki',
      nameJa: '双手突',
      techniqueId: 'goshin-morote-tsuki',
      groupId: 'staff',
      criteria: [
        c('goshin-morote-tsuki-1', 'Uke thrusts staff toward solar plexus (kiai).', 'small_medium'),
        c('goshin-morote-tsuki-2', 'Tori Tai-sabaki deflects, controls staff/arm lock, throws Uke and points staff toward Uke; Uke doesn’t stand up.', 'small_medium'),
      ],
    },
    {
      id: 'goshin-shomen-zuke',
      kind: 'technique',
      name: 'Shomen-zuke',
      nameJa: '正面付',
      techniqueId: 'goshin-shomen-zuke',
      groupId: 'pistol',
      criteria: [
        c('goshin-shomen-zuke-1', 'Uke orders hands up and presses pistol muzzle into Tori’s abdomen.', 'small_medium'),
        c('goshin-shomen-zuke-2', 'When Uke looks down, Tori dodges muzzle, controls barrel/wrist, and takes the pistol using hip movement.', 'small_medium'),
      ],
    },
    {
      id: 'goshin-koshi-gamae',
      kind: 'technique',
      name: 'Koshi-gamae',
      nameJa: '腰構',
      techniqueId: 'goshin-koshi-gamae',
      groupId: 'pistol',
      criteria: [
        c('goshin-koshi-gamae-1', 'Uke points pistol from the hip at Tori’s abdomen after hands-up order.', 'small_medium'),
        c('goshin-koshi-gamae-2', 'Tori twists, takes pistol with both hands, then hits Uke’s face with pistol grip (kiai).', 'small_medium'),
      ],
    },
    {
      id: 'goshin-haimen-zuke',
      kind: 'technique',
      name: 'Haimen-zuke',
      nameJa: '背面付',
      techniqueId: 'goshin-haimen-zuke',
      groupId: 'pistol',
      criteria: [
        c('goshin-haimen-zuke-1', 'Uke from behind presses pistol at middle of Tori’s back after hands-up order.', 'small_medium'),
        c('goshin-haimen-zuke-2', 'Tori rotates to dodge, controls forearm/pistol, throws Uke and takes the pistol.', 'small_medium'),
      ],
    },
  ],
}
